<template>
  <div class="hris-page">
    <div class="hris-page-inner">
      <div class="hris-page-header">
        <span class="hris-page-eyebrow">Payroll</span>
        <h1 class="hris-page-title">Payroll Overview</h1>
        <p class="hris-page-subtitle">{{ yearfilter }} yearly summary of cost, attendance, and pay trends.</p>
      </div>

      <div class="hris-toolbar">
        <q-select outlined dense color="green" v-model="yearfilter" :options="yearOptions" label="Year" style="width: 150px" @input="fetchOverview"></q-select>
        <div class="hris-toolbar-spacer"></div>
        <q-btn unelevated class="hris-q-primary" icon="fas fa-sync-alt" label="Refresh" :loading="loading" @click="fetchOverview"></q-btn>
      </div>

      <!-- Main Charts Row -->
      <div class="row q-col-gutter-md q-mb-md justify-center">
        <div class="col-12 col-md-6">
          <div class="hris-panel">
            <div class="hris-panel-title">Cost Distribution (Monthly)</div>
            <apexchart height="280" type="bar" :options="costChartOptions" :series="costChartSeries"></apexchart>
          </div>
        </div>
        <div class="col-12 col-md-6">
          <div class="hris-panel">
            <div class="hris-panel-title">Attendance Impact</div>
            <apexchart height="280" type="bar" :options="impactChartOptions" :series="attendanceImpactSeries"></apexchart>
          </div>
        </div>
      </div>

      <!-- Secondary Charts Row -->
      <div class="row q-col-gutter-md q-mb-md">
        <div class="col-12 col-md-6">
          <div class="hris-panel">
            <div class="hris-panel-title">Accepted Leaves Per Month</div>
            <apexchart height="300" type="bar" :options="leaveTrendOptions" :series="leaveTrendSeries"></apexchart>
          </div>
        </div>
        <div class="col-12 col-md-6">
          <div class="hris-panel">
            <div class="hris-panel-title">Net Pay Trend</div>
            <apexchart height="300" type="bar" :options="netPayTrendOptions" :series="netPayTrendSeries"></apexchart>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import VueApexCharts from 'vue-apexcharts'

export default {
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    return {
      loading: false,
      yearfilter: new Date().getFullYear(),
      yearOptions: [],
      totals: {
        employees: 0,
        processedEmployees: 0,
        grossPay: 0,
        netPay: 0,
        absentPay: 0,
        tardinessPay: 0,
        overtimePay: 0,
      },
      monthlyTrends: [],
      apiUrl: process.env.VUE_APP_API_URL,
    }
  },

  computed: {
    costChartSeries() {
      return [
        { name: 'Net Pay', data: this.monthlyTrends.map(m => m.netPay) },
        { name: 'Deductions', data: this.monthlyTrends.map(m => m.grossPay - m.netPay) }
      ]
    },

    costChartOptions() {
      return {
        chart: { stacked: true, toolbar: { show: false } },
        xaxis: { categories: this.monthlyTrends.map(m => m.monthName) },
        colors: ['#0f766e', '#b91c1c'],
        legend: { position: 'bottom' },
        yaxis: {
          labels: { formatter: (val) => '₱' + val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }
        },
        dataLabels: { 
          enabled: false
        },
        tooltip: {
          y: { formatter: (val) => '₱' + val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }
        },
      }
    },

    attendanceImpactSeries() {
      return [
        { name: 'Absences', data: this.monthlyTrends.map(m => m.absentPay) },
        { name: 'Tardiness', data: this.monthlyTrends.map(m => m.tardinessPay) },
        { name: 'Overtime', data: this.monthlyTrends.map(m => m.overtimePay) }
      ]
    },

    impactChartOptions() {
      return {
        chart: { toolbar: { show: false }, stacked: true },
        plotOptions: {
          bar: { borderRadius: 4, columnWidth: '60%' }
        },
        colors: ['#ef4444', '#f59e0b', '#4f46e5'],
        xaxis: {
          categories: this.monthlyTrends.map(m => m.monthName)
        },
        yaxis: {
          labels: { formatter: (val) => '₱' + val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }
        },
        legend: { position: 'bottom' },
        dataLabels: { enabled: false },
        tooltip: {
          y: { formatter: (val) => '₱' + val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }
        }
      }
    },

    leaveTrendSeries() {
      return [{
        name: 'Accepted Leaves',
        data: this.monthlyTrends.map(m => m.acceptedLeaves)
      }];
    },

    leaveTrendOptions() {
      return {
        chart: { stacked: true, toolbar: { show: false } },
        xaxis: { categories: this.monthlyTrends.map(m => m.monthName) },
        colors: ['#10b981'],
        legend: { position: 'bottom' },
        yaxis: { labels: { formatter: (val) => Math.round(val) } },
        dataLabels: { enabled: false },
        tooltip: {
          y: { formatter: (val) => Math.round(val) }
        }
      }
    },

    netPayTrendSeries() {
      return [{
        name: 'Net Pay',
        data: this.monthlyTrends.map(m => m.netPay)
      }];
    },

    netPayTrendOptions() {
      return {
        chart: { stacked: true, toolbar: { show: false } },
        xaxis: { categories: this.monthlyTrends.map(m => m.monthName) },
        colors: ['#0f766e'],
        legend: { position: 'bottom' },
        yaxis: {
          labels: { formatter: (val) => '₱' + val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }
        },
        dataLabels: { enabled: false },
        tooltip: {
          y: { formatter: (val) => '₱' + val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }
        }
      }
    }
  },

  created() {
    this.yearsList()
    this.fetchOverview()
  },

  methods: {
    yearsList() {
      axios.create({
        baseURL: this.apiUrl,
        headers: { 'Authorization': `Bearer ${this.token}` }
      }).post('/timekeeping/years/list')
        .then((res) => {
          this.yearOptions = res.data.data
        })
    },

    fetchOverview() {
      this.loading = true
      axios.get(this.apiUrl + '/payroll/overview/yearly', {
        headers: { 'Authorization': `Bearer ${this.token}` },
        params: {
          year: this.yearfilter,
        }
      }).then((response) => {
        const data = response.data.data || {}
        this.totals = data.totals || this.totals
        this.monthlyTrends = data.monthlyTrends || []
      }).finally(() => {
        this.loading = false
      })
    },

    formatCurrency(value) {
      const num = parseFloat(value) || 0;
      return 'PHP ' + num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }
}
</script>

<style scoped>
.hris-panel {
  height: 100%;
}
</style>
